import { KeyboardDateTimePicker } from "@material-ui/pickers";
import React, { useCallback, useMemo } from "react";
import CcFieldLayout, {
  resolveFieldMessage,
  useCcInputStyles,
} from "./CcFieldLayout";

function parseDateTimeLocalValue(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const text = String(value).trim();
  if (!text) return null;
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (match) {
    const [, year, month, day, hours, minutes] = match;
    const date = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hours),
      Number(minutes),
    );
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const fallback = new Date(text);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function formatDateTimeLocalValue(date) {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

const CcDateTimePicker = React.memo(
  ({
    label,
    required = false,
    value,
    onChange,
    format = "dd/MM/yyyy, hh:mm aa",
    clearable = true,
    error,
    helperText,
    hint,
    id,
    onBlur,
    KeyboardButtonProps,
    InputLabelProps: _inputLabelProps,
    ...props
  }) => {
    const inputClasses = useCcInputStyles();
    const { hasError, message } = resolveFieldMessage({
      error,
      helperText,
      hint,
    });
    const parsedValue = useMemo(() => parseDateTimeLocalValue(value), [value]);
    const handleChange = useCallback(
      (newValue) => {
        onChange?.(formatDateTimeLocalValue(newValue));
      },
      [onChange],
    );

    const picker = (
      <KeyboardDateTimePicker
        autoOk
        ampm
        variant="inline"
        inputVariant="outlined"
        format={format}
        clearable={clearable}
        value={parsedValue}
        onChange={handleChange}
        onBlur={onBlur}
        id={id}
        error={hasError}
        helperText={undefined}
        label={undefined}
        className={inputClasses.root}
        InputLabelProps={{ shrink: false, style: { display: "none" } }}
        FormHelperTextProps={{ style: { display: "none" } }}
        placeholder="dd/mm/yyyy, hh:mm am"
        KeyboardButtonProps={{
          "aria-label": "change date and time",
          ...KeyboardButtonProps,
        }}
        {...props}
      />
    );

    if (!label && !message) {
      return picker;
    }

    return (
      <CcFieldLayout
        label={label}
        required={required}
        error={hasError ? message : undefined}
        hint={!hasError ? message : undefined}
        htmlFor={id}
      >
        {picker}
      </CcFieldLayout>
    );
  },
);

CcDateTimePicker.displayName = "CcDateTimePicker";

export default CcDateTimePicker;
